import React, { FormEvent, useMemo, useState } from "react";
import { Eye, EyeOff, FileInput, Plus } from "lucide-react";
import {
  ActivityLog,
  EventOperationsState,
  RegistrationFormField,
} from "../../types";
import {
  createEventEntityId,
  getVisibleRegistrationFields,
} from "../../lib/eventOperations";

interface Props {
  state: EventOperationsState;
  onChange: (state: EventOperationsState) => void;
  onClose: () => void;
}
const inputClass =
  "w-full h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-300";

export const EventRegistrationFormBuilder: React.FC<Props> = ({
  state,
  onChange,
  onClose,
}) => {
  const [editing, setEditing] = useState<RegistrationFormField | "new" | null>(
    "new",
  );
  const [previewProfession, setPreviewProfession] = useState("Médico");
  const visible = useMemo(
    () =>
      getVisibleRegistrationFields(state.registrationFields, {
        "field-profession": previewProfession,
      }),
    [state.registrationFields, previewProfession],
  );
  const withActivity = (
    fields: RegistrationFormField[],
    action: string,
    description: string,
  ) => {
    const activity: ActivityLog = {
      id: createEventEntityId("log"),
      eventId: state.event.id,
      entityType: "registration_form",
      entityId: state.event.id,
      action,
      description,
      createdAt: new Date().toISOString(),
    };
    onChange({
      ...state,
      registrationFields: fields,
      activityLogs: [activity, ...state.activityLogs],
      updatedAt: activity.createdAt,
    });
  };
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const current = editing === "new" ? null : editing;
    const conditionalFieldId = String(data.get("conditionalFieldId") || "");
    const requestedType = String(
      data.get("type") || "text",
    ) as RegistrationFormField["type"];
    const field: RegistrationFormField = {
      id: current?.id || createEventEntityId("field"),
      label: String(data.get("label") || "").trim(),
      type: requestedType === "file" ? "text" : requestedType,
      required: data.get("required") === "on",
      options: String(data.get("options") || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      conditionalOn: conditionalFieldId
        ? {
            fieldId: conditionalFieldId,
            equals: String(data.get("conditionalValue") || "").trim(),
          }
        : undefined,
      placeholder:
        String(data.get("placeholder") || "").trim() ||
        (requestedType === "file" ? "https://…" : undefined),
      helpText:
        String(data.get("helpText") || "").trim() ||
        (requestedType === "file"
          ? "Pega un enlace verificable hasta conectar almacenamiento."
          : undefined),
      active: data.get("active") === "on",
      sortOrder: current?.sortOrder ?? state.registrationFields.length + 1,
    };
    const fields = current
      ? state.registrationFields.map((item) =>
          item.id === current.id ? field : item,
        )
      : [...state.registrationFields, field];
    withActivity(
      fields,
      current ? "field_updated" : "field_created",
      `${current ? "Campo actualizado" : "Campo creado"}: ${field.label}.`,
    );
    setEditing(null);
  };
  const toggle = (field: RegistrationFormField) => {
    const active = field.active === false;
    withActivity(
      state.registrationFields.map((item) =>
        item.id === field.id ? { ...item, active } : item,
      ),
      active ? "field_enabled" : "field_disabled",
      `${active ? "Campo activado" : "Campo ocultado"}: ${field.label}.`,
    );
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-slate-950/60 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-5xl max-h-[92dvh] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        <header className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display font-black flex items-center gap-2">
              <FileInput size={18} className="text-emerald-600" /> Formulario de
              inscripción
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Campos condicionales sin duplicar datos del perfil.
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 px-3 rounded-xl border border-slate-200 text-xs font-black"
          >
            Cerrar
          </button>
        </header>
        <div className="p-4 overflow-y-auto grid lg:grid-cols-[1.15fr_0.85fr] gap-4">
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase text-slate-500">
                Campos
              </h3>
              <button
                onClick={() => setEditing("new")}
                className="h-9 px-3 rounded-xl bg-slate-950 text-white text-xs font-black flex items-center gap-1.5"
              >
                <Plus size={14} /> Campo
              </button>
            </div>
            {state.registrationFields
              .slice()
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              .map((field) => (
                <div
                  key={field.id}
                  className="rounded-xl border border-slate-200 p-3 flex items-center gap-3"
                >
                  <button
                    onClick={() => toggle(field)}
                    title={field.active === false ? "Activar" : "Ocultar"}
                    className={`w-9 h-9 rounded-xl flex items-center justify-center ${field.active === false ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-700"}`}
                  >
                    {field.active === false ? (
                      <EyeOff size={15} />
                    ) : (
                      <Eye size={15} />
                    )}
                  </button>
                  <button
                    onClick={() => setEditing(field)}
                    className="flex-1 text-left"
                  >
                    <p className="text-xs font-black">
                      {field.label}
                      {field.required ? " *" : ""}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {field.type}
                      {field.conditionalOn
                        ? ` · si ${state.registrationFields.find((item) => item.id === field.conditionalOn?.fieldId)?.label || field.conditionalOn.fieldId} = ${field.conditionalOn.equals}`
                        : ""}
                    </p>
                  </button>
                </div>
              ))}
          </section>
          <section className="space-y-4">
            {editing && (
              <form
                onSubmit={save}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4 grid sm:grid-cols-2 gap-3"
              >
                <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
                  Etiqueta
                  <input
                    name="label"
                    required
                    defaultValue={editing === "new" ? "" : editing.label}
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Tipo
                  <select
                    name="type"
                    defaultValue={
                      editing === "new" || editing.type === "file"
                        ? "text"
                        : editing.type
                    }
                    className={`${inputClass} mt-1`}
                  >
                    {[
                      "text",
                      "textarea",
                      "select",
                      "date",
                      "checkbox",
                      "radio",
                    ].map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Placeholder
                  <input
                    name="placeholder"
                    defaultValue={editing === "new" ? "" : editing.placeholder}
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
                  Opciones separadas por coma
                  <input
                    name="options"
                    defaultValue={
                      editing === "new" ? "" : editing.options?.join(", ")
                    }
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Depende de
                  <select
                    name="conditionalFieldId"
                    defaultValue={
                      editing === "new" ? "" : editing.conditionalOn?.fieldId
                    }
                    className={`${inputClass} mt-1`}
                  >
                    <option value="">Siempre visible</option>
                    {state.registrationFields
                      .filter(
                        (item) =>
                          item.id !== (editing === "new" ? "" : editing.id),
                      )
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500">
                  Cuando sea
                  <input
                    name="conditionalValue"
                    defaultValue={
                      editing === "new" ? "" : editing.conditionalOn?.equals
                    }
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
                  Ayuda
                  <input
                    name="helpText"
                    defaultValue={editing === "new" ? "" : editing.helpText}
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="flex items-center gap-2 text-xs font-bold">
                  <input
                    name="required"
                    type="checkbox"
                    defaultChecked={
                      editing === "new" ? false : editing.required
                    }
                  />{" "}
                  Obligatorio
                </label>
                <label className="flex items-center gap-2 text-xs font-bold">
                  <input
                    name="active"
                    type="checkbox"
                    defaultChecked={
                      editing === "new" ? true : editing.active !== false
                    }
                  />{" "}
                  Activo
                </label>
                <div className="sm:col-span-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(null)}
                    className="h-9 px-3 rounded-xl border bg-white text-xs font-black"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="h-9 px-4 rounded-xl bg-emerald-500 text-xs font-black"
                  >
                    Guardar campo
                  </button>
                </div>
              </form>
            )}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="font-display font-black text-sm">
                    Vista condicional
                  </h3>
                  <p className="text-[10px] text-slate-500">
                    Comprueba qué campos verá cada perfil.
                  </p>
                </div>
                <select
                  value={previewProfession}
                  onChange={(event) => setPreviewProfession(event.target.value)}
                  className={`${inputClass} w-32`}
                >
                  <option>Médico</option>
                  <option>Estudiante</option>
                  <option>Consultor</option>
                </select>
              </div>
              <div className="mt-4 space-y-2">
                {visible.map((field) => (
                  <div
                    key={field.id}
                    className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2"
                  >
                    <p className="text-xs font-bold">
                      {field.label}
                      {field.required ? " *" : ""}
                    </p>
                    <p className="text-[9px] text-slate-400">{field.type}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
